import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ElementRef,
  HostListener,
  ChangeDetectorRef,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { countries } from 'src/app/constants/countryCodeData';

export interface CountryItem {
  country: string;
  code: string;
  iso: string;
  flagUrl?: string;
}

@Component({
  selector: 'app-phone-input',
  templateUrl: './app-phone-input.component.html',
  styleUrls: ['./app-phone-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppPhoneInputComponent),
      multi: true,
    },
  ],
})
export class AppPhoneInputComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() labelText: string = 'Phone Number';
  @Input() label: string = '';
  @Input() mandatory: boolean = false;
  @Input() required: boolean = false;
  @Input() optional: boolean = false;
  @Input() tooltip: string = '';
  @Input() labelIcon: string = '';
  @Input() labelSize: 'sm' | 'md' | 'lg' = 'md';

  get resolvedLabel(): string {
    return this.label || this.labelText || 'Phone Number';
  }

  get isRequired(): boolean {
    return this.mandatory || this.required;
  }

  @Input() placeholder: string = 'eg: 9804XXXXXX';
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() errorMsg: string | null = null;
  @Input() helperText: string = '';

  @Input() defaultCountryIso: string = 'IN';
  @Input() countryCode: string = '91';
  @Input() size: 'sm' | 'md' | 'lg' | 'compact' = 'md';
  @Input() height: number | string = '';
  @Input() maxlength: number = 10;
  @Input() customClass: string = '';

  @Output() countryChange = new EventEmitter<CountryItem>();
  @Output() phoneChange = new EventEmitter<string>();
  @Output() inputBlur = new EventEmitter<FocusEvent>();
  @Output() inputFocus = new EventEmitter<FocusEvent>();

  value: string = '';
  isFocused: boolean = false;
  isDropdownOpen: boolean = false;
  searchTerm: string = '';

  countriesList: CountryItem[] = [];
  filteredCountries: CountryItem[] = [];
  selectedCountry: CountryItem | null = null;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private elementRef: ElementRef, private cdr: ChangeDetectorRef) {}

  get effectiveMaxLength(): number {
    if (this.maxlength !== undefined && this.maxlength !== null && this.maxlength > 0) {
      return this.maxlength;
    }
    const iso = (this.selectedCountry?.iso || this.defaultCountryIso || 'IN').toUpperCase();
    return iso === 'IN' ? 10 : 15;
  }

  ngOnInit(): void {
    this.initCountries();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['countryCode'] && this.countriesList.length > 0) {
      this.syncCountryByCode(this.countryCode);
    }
  }

  private initCountries(): void {
    this.countriesList = (countries || []).map((c: any) => ({
      ...c,
      flagUrl: `https://flagcdn.com/w40/${c.iso.toLowerCase()}.png`,
    }));
    this.filteredCountries = [...this.countriesList];

    if (this.countryCode) {
      this.syncCountryByCode(this.countryCode);
    } else {
      const defaultCountry =
        this.countriesList.find((c) => c.iso.toUpperCase() === this.defaultCountryIso.toUpperCase()) ||
        this.countriesList.find((c) => c.country === 'India') ||
        this.countriesList[0];
      if (defaultCountry) {
        this.selectCountry(defaultCountry, false);
      }
    }
  }

  private syncCountryByCode(code: string): void {
    const cleanCode = String(code).replace(/^\+/, '').trim();
    const found = this.countriesList.find((c) => c.code === cleanCode);
    if (found) {
      this.selectedCountry = found;
    }
  }

  writeValue(val: any): void {
    if (val !== undefined && val !== null) {
      let clean = String(val).replace(/\D/g, '');
      if (clean.length > this.effectiveMaxLength) {
        clean = clean.slice(0, this.effectiveMaxLength);
      }
      this.value = clean;
    } else {
      this.value = '';
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.isDropdownOpen = false;
    }
    this.cdr.markForCheck();
  }

  toggleDropdown(event?: MouseEvent): void {
    if (event) event.stopPropagation();
    if (this.disabled) return;
    this.isDropdownOpen = !this.isDropdownOpen;
    if (this.isDropdownOpen) {
      this.searchTerm = '';
      this.filteredCountries = [...this.countriesList];
    }
  }

  selectCountry(country: CountryItem, emit: boolean = true): void {
    this.selectedCountry = country;
    this.countryCode = country.code;
    this.isDropdownOpen = false;
    if (emit) {
      this.countryChange.emit(country);
    }
    this.cdr.markForCheck();
  }

  onSearch(event: any): void {
    this.searchTerm = event.target.value || '';
    const term = this.searchTerm.toLowerCase();
    this.filteredCountries = this.countriesList.filter(
      (c) =>
        c.country.toLowerCase().includes(term) ||
        c.code.includes(term) ||
        c.iso.toLowerCase().includes(term)
    );
  }

  onPhoneInput(event: any): void {
    let raw = event.target.value || '';
    raw = raw.replace(/\D/g, '');
    const max = this.effectiveMaxLength;
    if (raw.length > max) {
      raw = raw.slice(0, max);
    }
    this.value = raw;
    event.target.value = raw;
    this.onChange(raw);
    this.phoneChange.emit(raw);
  }

  onKeyDown(event: KeyboardEvent): void {
    if (
      event.key === 'Backspace' ||
      event.key === 'Tab' ||
      event.key === 'Delete' ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight' ||
      event.key === 'ArrowUp' ||
      event.key === 'ArrowDown' ||
      event.key === 'Home' ||
      event.key === 'End' ||
      event.key === 'Enter' ||
      event.key === 'Escape' ||
      event.ctrlKey ||
      event.metaKey
    ) {
      return;
    }

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    const target = event.target as HTMLInputElement;
    const hasSelection =
      target.selectionStart !== null &&
      target.selectionEnd !== null &&
      target.selectionStart !== target.selectionEnd;

    if (!hasSelection && (target.value || '').length >= this.effectiveMaxLength) {
      event.preventDefault();
    }
  }

  onBlur(event: FocusEvent): void {
    this.isFocused = false;
    this.onTouched();
    this.inputBlur.emit(event);
  }

  onFocus(event: FocusEvent): void {
    this.isFocused = true;
    this.inputFocus.emit(event);
  }

  trackByCountry(index: number, c: CountryItem): string {
    return c.iso || c.code || String(index);
  }

  get hasError(): boolean {
    return !!this.errorMsg && this.errorMsg.trim().length > 0;
  }

  get resolvedHeight(): string | null {
    if (this.height) {
      return typeof this.height === 'number' ? `${this.height}px` : this.height;
    }
    return null;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      if (this.isDropdownOpen) {
        this.isDropdownOpen = false;
      }
    }
  }
}
